import {isFeatureEnabled} from '@github-ui/feature-flags'
import {isModernPullRequestExperienceEnabled} from '@github-ui/pull-request-feature-flags'
import {publishOpenCopilotChat} from '@github-ui/copilot-chat-events'
import {CopilotChatIntents} from '@github-ui/copilot-chat-types'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {
  AlertIcon,
  CodeReviewIcon,
  CommentDiscussionIcon,
  CopilotIcon,
  GitMergeIcon,
  HistoryIcon,
  type Icon,
  InfoIcon,
  NoteIcon,
} from '@primer/octicons-react'
import {useCallback, useMemo} from 'react'

import {DockedPanel, type PanelMetadata} from '../../contexts/DockedPanelContext'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {useMergeStatusButtonData} from '../../hooks/use-merge-status-button-data'
import {useIsFeatureStateEnabled} from '../../hooks/use-feature-state'

type PanelSelectCallback = () => void

/**
 * Single source of truth for every docked side panel's metadata.
 *
 * Each entry pairs a panel id with its display heading and an enablement hook.
 * Panel components themselves are imported from `panel-render-map.tsx`, which
 * is only imported by `DockedSidePanelContent`. This keeps registry consumers
 * (heading lookups, enablement hooks, trigger buttons) free of every panel's
 * transitive module graph.
 */
interface PanelRegistryEntry {
  id: DockedPanel
  heading: string
  /** Leading octicon shown next to the panel's heading in navigation menus. */
  icon: Icon
  /**
   * Hook deciding whether this panel is currently available to the user.
   * Called once per render via `useDockedPanelEnablement()`; iteration order
   * over the registry is stable so React's rules-of-hooks are satisfied.
   */
  useIsEnabled: () => boolean
  /**
   * Optional extra behavior that runs after selecting this panel from the panel
   * switcher. The switcher itself still handles opening the panel uniformly.
   */
  useOnPanelSwitcherSelect?: () => PanelSelectCallback
  /**
   * Metadata the panel switcher passes to `openPanel` when this panel is
   * selected, so panels that attribute their opens (Manage batch) get the right
   * entry surface without the switcher knowing anything panel-specific.
   */
  openMetadata?: PanelMetadata
}

function alwaysEnabled(): boolean {
  return true
}

/**
 * Selecting Copilot from the panel switcher must also publish the
 * open-copilot-chat event so the global Copilot chat loader mounts
 * `DockedCopilotChatPortal`, which fills the docked body div. The
 * toolbar's `OpenCopilotChatPanelButton` already publishes this event,
 * but the panel switcher bypasses that button.
 */
function useCopilotChatPanelSwitcherSelect(): PanelSelectCallback {
  return useCallback(() => {
    publishOpenCopilotChat({intent: CopilotChatIntents.conversation})
  }, [])
}

function useManageBatchEnabled(): boolean {
  return isFeatureEnabled('unified_batch_pr_comments')
}

function useOverviewFeatureEnabled(): boolean {
  return useIsFeatureStateEnabled('overview')
}

function useMergeStatusEnabled(): boolean {
  const {
    data: {pullRequest, mergeStatusButtonData},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const validated = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  return !!validated
}

// Ordering determines the call order of useIsEnabled hooks during enablement
// evaluation, so it MUST be stable across renders.
const PANEL_REGISTRY: readonly PanelRegistryEntry[] = [
  {id: DockedPanel.Alerts, heading: 'Alerts', icon: AlertIcon, useIsEnabled: alwaysEnabled},
  {id: DockedPanel.Comments, heading: 'Comments', icon: CommentDiscussionIcon, useIsEnabled: alwaysEnabled},
  {
    id: DockedPanel.CopilotChat,
    heading: 'Copilot',
    icon: CopilotIcon,
    useIsEnabled: alwaysEnabled,
    useOnPanelSwitcherSelect: useCopilotChatPanelSwitcherSelect,
  },
  {id: DockedPanel.MergeStatus, heading: 'Merge status', icon: GitMergeIcon, useIsEnabled: useMergeStatusEnabled},
  {id: DockedPanel.Overview, heading: 'Overview', icon: InfoIcon, useIsEnabled: alwaysEnabled},
  {id: DockedPanel.Timeline, heading: 'Activity', icon: HistoryIcon, useIsEnabled: useOverviewFeatureEnabled},
  {
    id: DockedPanel.ManageBatchReviewComments,
    heading: 'Manage batch',
    icon: CodeReviewIcon,
    useIsEnabled: useManageBatchEnabled,
    openMetadata: {manageBatchEntrySurface: 'panel_switcher'},
  },
]

const PANEL_REGISTRY_BY_ID: Readonly<Record<DockedPanel, PanelRegistryEntry>> = Object.freeze(
  Object.fromEntries(PANEL_REGISTRY.map(entry => [entry.id, entry])) as Record<DockedPanel, PanelRegistryEntry>,
)

export function getPanelHeading(id: DockedPanel): string {
  // When the updated PR header is enabled, "Overview" is renamed to "Description".
  if (id === DockedPanel.Overview && isModernPullRequestExperienceEnabled()) {
    return 'Description'
  }
  return PANEL_REGISTRY_BY_ID[id].heading
}

/**
 * Lowercased panel name for embedding mid-sentence in aria-labels
 * (e.g. `Close ${name} panel`). Lowercased except for proper nouns
 * like "Copilot".
 */
export function getPanelLabelName(id: DockedPanel): string {
  if (id === DockedPanel.CopilotChat) return 'Copilot'
  return getPanelHeading(id).toLowerCase()
}

export function getPanelIcon(id: DockedPanel): Icon {
  // Mirror getPanelHeading's rename branch: "Description" uses the note icon.
  if (id === DockedPanel.Overview && isModernPullRequestExperienceEnabled()) {
    return NoteIcon
  }
  return PANEL_REGISTRY_BY_ID[id].icon
}

export function usePanelSwitcherSelectCallback(id: DockedPanel): PanelSelectCallback | undefined {
  return PANEL_REGISTRY_BY_ID[id].useOnPanelSwitcherSelect?.()
}

/** Metadata the panel switcher should pass to `openPanel` for this panel, if any. */
export function getPanelOpenMetadata(id: DockedPanel): PanelMetadata | undefined {
  return PANEL_REGISTRY_BY_ID[id].openMetadata
}

/**
 * Evaluates every panel's `useIsEnabled()` hook in registry order and returns a
 * stable mapping from panel id → enabled. The registry array is static, so the
 * call order of the hooks is identical on every render.
 */
function useDockedPanelEnablement(): Readonly<Record<DockedPanel, boolean>> {
  const enablement = {} as Record<DockedPanel, boolean>
  for (const entry of PANEL_REGISTRY) {
    enablement[entry.id] = entry.useIsEnabled()
  }
  return enablement
}

/** Returns the currently enabled panel ids sorted alphabetically by display heading. */
export function useEnabledDockedPanelIds(): DockedPanel[] {
  const enablement = useDockedPanelEnablement()
  // Stable string key over the enablement bitmap so consumers (and the
  // DockedPanelEnabledContext value) see a stable array reference across
  // renders when nothing has changed.
  const enablementKey = PANEL_REGISTRY.map(entry => (enablement[entry.id] ? '1' : '0')).join('')
  return useMemo(
    () =>
      // Read enablement from the key (positional bitmap) so the memo depends only
      // on `enablementKey` — PANEL_REGISTRY and getPanelHeading are module-level constants.
      PANEL_REGISTRY.filter((_entry, index) => enablementKey[index] === '1')
        .map(entry => entry.id)
        .sort((a, b) => getPanelHeading(a).localeCompare(getPanelHeading(b))),
    [enablementKey],
  )
}
